import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import gradeFrag from './grade.frag.glsl?raw';

const gradeVert = `varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`;

export interface FrameInfo {
  /** internal render width and height in device pixels */
  width: number;
  height: number;
  /** current adaptive resolution scale */
  scale: number;
}

/**
 * The cinematic chain every act renders through: HDR scene → bloom → grade → ACES output.
 * Internal resolution adapts to hold the frame budget.
 */
export class CinematicComposer {
  readonly renderer: THREE.WebGLRenderer;
  readonly composer: EffectComposer;
  readonly bloom: UnrealBloomPass;
  readonly grade: ShaderPass;
  private renderPass: RenderPass;
  private scale = 1;
  private qualityScale = 1;
  private acc = 0;
  private n = 0;
  private lastW = 0;
  private lastH = 0;
  readonly dpr: number;

  constructor(readonly canvas: HTMLCanvasElement) {
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: false, alpha: false, powerPreference: 'high-performance' });
    this.renderer.setPixelRatio(1);
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.15;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    const target = new THREE.WebGLRenderTarget(2, 2, { type: THREE.HalfFloatType });
    this.composer = new EffectComposer(this.renderer, target);
    this.renderPass = new RenderPass(new THREE.Scene(), new THREE.Camera());
    this.bloom = new UnrealBloomPass(new THREE.Vector2(2, 2), 1.0, 0.6, 0.7);
    this.grade = new ShaderPass({
      uniforms: { tDiffuse: { value: null }, time: { value: 0 }, vignette: { value: 0.4 }, aberration: { value: 0.009 }, grain: { value: 0.011 } },
      vertexShader: gradeVert,
      fragmentShader: gradeFrag
    });
    this.composer.addPass(this.renderPass);
    this.composer.addPass(this.bloom);
    this.composer.addPass(this.grade);
    this.composer.addPass(new OutputPass());
  }

  /** Point the chain at a scene and camera. */
  setScene(scene: THREE.Scene, camera: THREE.Camera) {
    this.renderPass.scene = scene;
    this.renderPass.camera = camera;
  }

  /** Fast mode halves the internal resolution on top of the adaptive scale. */
  setQuality(cinematic: boolean) {
    this.qualityScale = cinematic ? 1 : 0.7;
  }

  /** Call once per frame with the frame delta; returns the internal size used this frame. */
  beginFrame(dt: number, t: number): FrameInfo {
    if (dt > 0) {
      this.acc += dt; this.n++;
      if (this.n >= 30) {
        // the picture moves slowly, so sharpness matters more than frame rate: hold 25 fps or better before giving up resolution
        const avg = this.acc / this.n; this.acc = 0; this.n = 0;
        if (avg > 0.045) this.scale = Math.max(0.6, this.scale - 0.1);
        else if (avg < 0.028) this.scale = Math.min(1, this.scale + 0.05);
      }
    }
    const cw = this.canvas.clientWidth || 1, ch = this.canvas.clientHeight || 1;
    const w = Math.max(2, Math.round(cw * this.dpr * this.scale * this.qualityScale));
    const h = Math.max(2, Math.round(ch * this.dpr * this.scale * this.qualityScale));
    if (w !== this.lastW || h !== this.lastH) {
      this.lastW = w; this.lastH = h;
      this.renderer.setSize(w, h, false);
      this.composer.setSize(w, h);
    }
    (this.grade.uniforms.time as THREE.IUniform).value = t;
    return { width: w, height: h, scale: this.scale * this.qualityScale };
  }

  render() {
    this.composer.render();
  }
}
